import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { LeaveRow, LeaveStatus, OrgLeaveManagementApi } from '@/services/org-leave-management.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { formatDate, formatDateTime } from '@/lib/format';

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

const statusLabel: Record<LeaveStatus, string> = { 0: 'Pending', 1: 'Approved', 2: 'Rejected' };
const statusTone: Record<LeaveStatus, 'amber' | 'green' | 'red'> = { 0: 'amber', 1: 'green', 2: 'red' };

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Org tier — Staff Leaves, the approve/reject queue for a school in scope. Matches real legacy's `/leave/request` filters/columns. */
export function OrgStaffLeavePage() {
  const qc = useQueryClient();
  const [schoolId, setSchoolId] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeaveStatus | ''>('');
  const [month, setMonth] = useState<number | ''>('');
  const [userId, setUserId] = useState('');

  const { data: schools = [] } = useQuery({ queryKey: ['org-leave-schools'], queryFn: () => OrgLeaveManagementApi.schools() });
  const { data: staffOptions = [] } = useQuery({
    queryKey: ['org-leave-staff-options', schoolId],
    queryFn: () => OrgLeaveManagementApi.staffOptions(schoolId),
    enabled: !!schoolId,
  });

  const params = { status: statusFilter === '' ? undefined : statusFilter, month: month === '' ? undefined : month, userId: userId || undefined };

  const { data: rows = [], isFetching, error } = useQuery({
    queryKey: ['org-staff-leave', schoolId, params],
    queryFn: () => OrgLeaveManagementApi.list(schoolId, params),
    enabled: !!schoolId,
  });

  const updateStatus = useMutation({
    mutationFn: (v: { id: string; status: LeaveStatus }) => OrgLeaveManagementApi.updateStatus(v.id, schoolId, v.status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-staff-leave', schoolId] }),
  });

  function handleSchoolChange(v: string) {
    setSchoolId(v);
    setUserId('');
  }

  return (
    <div>
      <PageHeader title="Staff Leaves" description="Review and approve or reject staff leave requests for a school in your organization." />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select className="w-56" value={schoolId} onChange={(e) => handleSchoolChange(e.target.value)}>
          <option value="">— Select a school —</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select className="w-36" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value === '' ? '' : (Number(e.target.value) as LeaveStatus))} disabled={!schoolId}>
          <option value="">Filter: All</option>
          <option value={0}>Pending</option>
          <option value={1}>Approved</option>
          <option value={2}>Rejected</option>
        </Select>
        <Select className="w-36" value={month} onChange={(e) => setMonth(e.target.value === '' ? '' : Number(e.target.value))} disabled={!schoolId}>
          <option value="">Month: All</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </Select>
        <Select className="w-44" value={userId} onChange={(e) => setUserId(e.target.value)} disabled={!schoolId}>
          <option value="">Staff: All</option>
          {staffOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name ?? '—'}
            </option>
          ))}
        </Select>
      </div>

      {!schoolId ? (
        <div className="card p-8 text-center text-sm text-slate-400">Select a school to view its leave requests.</div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{errMsg(error)}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-slate-600">No.</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Name</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">From Date</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">To Date</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Total</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Reason</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Status</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Created At</th>
                <th className="px-4 py-2 text-right font-medium text-slate-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r: LeaveRow, i: number) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-500">{i + 1}</td>
                  <td className="px-4 py-2 font-medium text-slate-800">{r.userName ?? '—'}</td>
                  <td className="px-4 py-2 text-slate-500">{formatDate(r.fromDate)}</td>
                  <td className="px-4 py-2 text-slate-500">{formatDate(r.toDate)}</td>
                  <td className="px-4 py-2 text-slate-600">{r.days}</td>
                  <td className="max-w-xs truncate px-4 py-2 text-slate-600">{r.reason}</td>
                  <td className="px-4 py-2">
                    <Badge tone={statusTone[r.status]}>{statusLabel[r.status]}</Badge>
                  </td>
                  <td className="px-4 py-2 text-slate-500">{formatDateTime(r.createdAt)}</td>
                  <td className="px-4 py-2 text-right">
                    {r.status === 0 ? (
                      <span className="inline-flex items-center gap-1.5">
                        <button
                          type="button"
                          className="rounded p-1 text-green-600 hover:bg-green-50"
                          title="Approve"
                          disabled={updateStatus.isPending}
                          onClick={() => updateStatus.mutate({ id: r.id, status: 1 })}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="rounded p-1 text-red-600 hover:bg-red-50"
                          title="Reject"
                          disabled={updateStatus.isPending}
                          onClick={() => updateStatus.mutate({ id: r.id, status: 2 })}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {!isFetching && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-sm text-slate-400">
                    No matching records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
